import {
  Box,
  Button,
  Center,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  type UseModalProps,
} from "@chakra-ui/react";
import { InputDatePicker, NumberInput, Select } from "@components/ui-kit";
import { Trans, t } from "@lingui/macro";
import {
  useModalGetCreditDependenciesQuery,
  useTransferCreditMutation,
} from "@src/__generated__/graphql";
import { can } from "@src/utils/components/permissions";
import { currency } from "@src/utils/formatters";
import {
  fieldNumberModelToInputProps,
  fieldToSelectProps,
} from "@src/utils/forms/inputHelpers";
import { useStore } from "@src/utils/hooks";
import { NumberModel } from "@src/utils/models";
import { observer } from "mobx-react-lite";
import type React from "react";
import { type FC, useState } from "react";
import { Store } from "./Store";

interface ModalGetCreditProps {
  brandId: string;
  creditAccountId: string;
  outgoingInvoiceId?: string;
  credit: number;
  onSubmit?: () => void;
}

export const ModalGetCredit: FC<
  React.PropsWithChildren<ModalGetCreditProps & UseModalProps>
> = observer(function ModalGetCredit({
  brandId,
  creditAccountId,
  outgoingInvoiceId,
  credit,
  onSubmit,
  ...modalProps
}) {
  const appStore = useStore();
  const [store] = useState(() => new Store(appStore));
  const { form } = store;

  const { loading: loadingDependencies } = useModalGetCreditDependenciesQuery({
    variables: { id: brandId },
    skip: modalProps.isOpen === false,
    onCompleted(data) {
      if (!data) return;
      store.setDependencies(data);
    },
  });

  const [transferCredit, { loading: fetching }] = useTransferCreditMutation();

  const onGetCredit = async () => {
    if (form.from_credit_account_id.value) {
      const maxAmount = store.creditAccounts.find(
        ({ creditAccount }) =>
          creditAccount.id === form.from_credit_account_id.value,
      )?.stats.remaining_credit;

      if (maxAmount) {
        form.maxAmount = new NumberModel(
          maxAmount,
          appStore.workspaceStore.settings!.currency.denomination,
        );
      }
    }

    const { hasError } = await form.validate();

    if (hasError) {
      return;
    }

    transferCredit({
      variables: {
        to_credit_account_id: creditAccountId,
        outgoing_invoice_id: outgoingInvoiceId,
        ...form.serialize(),
      },
    })
      .then(({ data }) => {
        if (!data?.transferCredit) return;

        onCloseWrapper();
        onSubmit?.();
        appStore.UIStore.toast({
          title: t`Credit was successfully moved`,
          status: "success",
        });
      })
      .catch((error) => {
        if (!("message" in error)) return;
        appStore.UIStore.toast({
          title: error.message,
          status: "error",
        });
      });
  };

  const onCloseWrapper = () => {
    modalProps.onClose();
    form.reset();
  };

  return (
    <Modal
      isOpen={modalProps.isOpen}
      onClose={onCloseWrapper}
      size="xl"
      trapFocus={false}
    >
      <ModalOverlay />
      <ModalContent>
        {loadingDependencies && (
          <Center
            pos="absolute"
            zIndex={100}
            top="0"
            right="0"
            bottom="0"
            left="0"
            bg="white"
            rounded="3xl"
          >
            <Spinner color="teal.500" size="lg" />
          </Center>
        )}
        <ModalHeader>{t`Get credit`}</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <HStack align="baseline" mb="6">
            <span>
              <Trans>Current credit</Trans>:
            </span>
            <span className="font-bold text-2xl ml-2">
              {currency.formatByWorkspace(credit)}
            </span>
          </HStack>

          <Box pb="44">
            <div className="mb-4">
              <Select
                label={t`Credit account`}
                placeholder={t`Select credit account`}
                {...fieldToSelectProps(
                  form.from_credit_account_id,
                  store.creditAccountsOptions,
                )}
              />
            </div>
            <div className="mb-4">
              <NumberInput
                currency
                suffix={appStore.workspaceStore.settings?.currency.code}
                {...fieldNumberModelToInputProps(form.amount)}
                label={t`Credit amount`}
              />
            </div>
            <div className="mb-4">
              <InputDatePicker
                label={t`Transaction date`}
                isDisabled={
                  !appStore.workspaceStore.settings
                    ?.can_transfer_credit_with_date_without_constraints &&
                  can("credit_transfer_withDate")
                }
                selected={store.form.performed_at.$}
                onChange={(range) => {
                  if (!range?.start) return;

                  store.form.performed_at.value = range.start;
                }}
              />
            </div>
          </Box>
        </ModalBody>

        <ModalFooter>
          <Button colorScheme="grey" onClick={onCloseWrapper} variant="outline">
            <Trans>Cancel</Trans>
          </Button>
          <Button ml={3} isLoading={fetching} onClick={onGetCredit}>
            <Trans>Get credit</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
